import React, { useEffect, useState } from 'react';
import styled, { x } from '@xstyled/styled-components';
import Door from '../door.svg';
import { DoorWrapper } from '../DoorWrapper';
import { Item, ItemDefinition, RenderProps, registry } from '../../../Item';
import { QUIZ_DOOR } from '../../../itemIds';
import audioEffectOpenDoor from '~/assets/mp3/door-opening.mp3';
import { useDoorsStore } from '../../DoorsStore';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { QuizButton } from '~/ui/buttons/QuizButton';
import DoorBadgeImage from '../door-badge.svg';
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';

const StyledDoor = styled(Door)<{ title?: string }>`
  width: 100%;
  height: 100%;
  -webkit-tap-highlight-color: transparent;
  box-sizing: content-box;
  border-left: 12px solid transparent;
  margin-left: -12px;
`;

const DoorBadge = styled.div`
  position: absolute;
  top: 0;
  display: flex;
  justicy-content: center;
  align-items: center;

  h3 {
    max-width: 123px;
    width: 100%;
    text-align: center;
    position: absolute;
  }

  @media (max-width: 500px) {
    svg {
      width: 100%;
    }
    h3 {
      font-size: 13px;
    }
  }

  @media (max-width: 420px) {
    svg {
      width: 70px;
      margin-top: -25px;
    }
    h3 {
      margin-top: -25px;
    }
  }
`;

const StyledDoorWrapper = styled(DoorWrapper)`
  display: flex;
  justify-content: center;
`;

interface Props {
  id: string;
  groupId: string;
  imageUrl: string;
  name: string;
  correct: boolean;
}

export type QuizDoorDefinition = ItemDefinition<typeof QUIZ_DOOR, Props>;

export class QuizDoor extends Item<typeof QUIZ_DOOR, Props> {
  protected render({
    id,
    groupId,
    name,
    correct,
    imageUrl
  }: RenderProps<Props>) {
    const { editorMode, contentEditable, isReadOnlyView } = useEditorStore();
    const [isDoorOpened, setDoorIsOpened] = useState(false);
    const { addBlocker, removeBlocker, setAnswer } = useSequenceStore();
    const { setQuizAnswer } = useDoorsStore();
    const [playAudioOpenDoor] = useAudioEffect(audioEffectOpenDoor);
    const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
    const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);

    useEffect(() => {
      correct && addBlocker(id);
    }, []);

    // Show content directly in read-only mode
    if (isReadOnlyView) {
      return (
        <StyledDoorWrapper url={imageUrl}>
          <StyledDoor
            className="swing-right-fwd"
            role="img"
            aria-label={name}
          />
          <x.div
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            zIndex={10}
          >
            <QuizButton
              as="div"
              whiteSpace="nowrap"
              variant={correct ? 'correct' : 'wrong'}
              mx={6}
              padding={{ _: '0px 10px', sm: '5px 15px' }}
              fontSize={{ _: 16, sm: 24 }}
              w={'fit-content'}
            >
              {name}
            </QuizButton>
          </x.div>
        </StyledDoorWrapper>
      );
    }

    const handleOpenDoor = () => {
      if (!isDoorOpened) {
        playAudioOpenDoor();
        setDoorIsOpened(true);
        setTimeout(() => {
          if (!isDoorOpened) {
            correct
              ? playAudioEffectRightAnswer()
              : playAudioEffectWrongAnswer();
            setQuizAnswer(
              <QuizButton
                as="span"
                whiteSpace="nowrap"
                variant={correct ? 'correct' : 'wrong'}
                mx={6}
                padding={{ _: '0px 10px', sm: '5px 15px' }}
                fontSize={{ _: 16, sm: 24 }}
                display={'inline'}
                w={'fit-content'}
                role="alertdialog"
                aria-live="assertive"
              >
                {name}
              </QuizButton>
            );
            removeBlocker(id);
            setAnswer(groupId, id);
          }
        }, 400);
      }
    };

    return (
      <StyledDoorWrapper
        tabIndex={0}
        url={imageUrl}
        onClick={() => handleOpenDoor()}
      >
        <StyledDoor
          className={isDoorOpened ? 'swing-right-fwd' : ''}
          role="img"
          aria-label={t`Open the door`}
          title={t`Open the door`}
        />
        {!isDoorOpened && (
          <DoorBadge>
            <x.h3>{name}</x.h3>
            <DoorBadgeImage />
          </DoorBadge>
        )}
      </StyledDoorWrapper>
    );
  }
}

registry[QUIZ_DOOR] = QuizDoor;
